/**
 * Búsqueda de trazabilidad del pipeline. La consulta no se dispara al montar: la pantalla
 * arranca sin resultados y solo llama al backend cuando el administrador busca.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { traceApi } from '@/api/client';
import type { TraceQueryParams, TraceResponse } from '@/api/types';

interface UsePipelineTraceResult {
  data: TraceResponse | null;
  isPending: boolean;
  error: Error | null;
  search: (params: TraceQueryParams) => void;
  reset: () => void;
}

export function usePipelineTrace(): UsePipelineTraceResult {
  const [data, setData] = useState<TraceResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: (params: TraceQueryParams) => traceApi.getTrace(params),
    onSuccess: (response) => {
      setData(response);
      setError(null);
    },
    onError: (mutationError: Error) => {
      setError(mutationError);
      setData(null);
    },
  });

  // Dependemos de mutation.mutate/mutation.reset (estables entre renders), no del objeto
  // `mutation` completo: ese objeto es nuevo en cada render de useMutation, y si search/reset
  // dependieran de él, un `useEffect(() => search(params), [search])` en la pantalla consumidora
  // dispararía peticiones en bucle (cada mutate() re-renderiza → nueva identidad de search).
  const search = useCallback(
    (params: TraceQueryParams) => { mutation.mutate(params); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.mutate],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.reset]);

  return { data, isPending: mutation.isPending, error, search, reset };
}
